/**
 * Page-lifetime "already shown" state for the skills changeboard.
 *
 * Show-once is split in two: Post Office stops delivering the message across page loads (the
 * template's `viewed-check-filter`), and this store covers a single page load, where the headless
 * placement is not refetched and so still holds the same message instance when Rovo Chat reopens.
 *
 * Not localStorage: `AtlBrowserStorageLocal.setItem` returns `Status.BLOCKED` and writes nothing
 * for keys that are not registered in the BSC data store, so the flag never persisted and every
 * Rovo Chat open looked like a first view.
 *
 * The sidebar can recreate this module when it closes and reopens (see
 * `MentionAgentsSpotlightAcknowledgementStore`), so the flag lives on the page global rather than
 * in module scope.
 */
const SKILLS_CHANGEBOARD_SHOWN_PAGE_STORE_KEY = Symbol.for('rovo-chat.skills-changeboard.shown');

const hasBeenShown = (): boolean =>
	Reflect.get(globalThis, SKILLS_CHANGEBOARD_SHOWN_PAGE_STORE_KEY) === true;

const markShown = (): void => {
	Reflect.set(globalThis, SKILLS_CHANGEBOARD_SHOWN_PAGE_STORE_KEY, true);
};

type SkillsChangeboardShownStore = {
	/** True once the changeboard has rendered during this page load. */
	hasBeenShown: typeof hasBeenShown;
	/** Suppresses the changeboard for later mounts on this page. */
	markShown: typeof markShown;
	/** Test-only: clears the page-global flag between cases. */
	reset: () => void;
};

export const skillsChangeboardShownStore: SkillsChangeboardShownStore = {
	hasBeenShown,
	markShown,
	reset: () => {
		Reflect.deleteProperty(globalThis, SKILLS_CHANGEBOARD_SHOWN_PAGE_STORE_KEY);
	},
};
